import React, { useCallback, useContext, useImperativeHandle, useState } from 'react';

import { MDText } from 'i18n-react';

import { Button, IconComponentFunction, IconText, InfoIcon } from '@motorway/mw-highway-code';
import { SIZE, VARIANT } from '@motorway/mw-highway-code/enums';

import { VehicleLookupContext } from 'Context/vehicleLookup';

import { EVENT } from 'Utilities/analytics/optimizely/constants';
import { useOptimizelyTrack } from 'Utilities/analytics/optimizely/useOptimizelyTrack';

import type { ServiceFeeTiersResponse } from 'Components/transactionFees/hooks/useServiceFeeData';
import { Modal } from 'Storybook/components/local/Modal/Modal';

import { ServiceFeeExplainer } from '../ServiceFeeExplainer/ServiceFeeExplainer';
import Texts from '../transactionFees.texts.json';

import styles from './ServiceFeeAppliesNotice.scss';

const LocalT = new MDText(Texts);

export type ServiceFeeAppliesNoticeHandle = {
  openModal: () => void;
};

type ServiceFeeAppliesNoticeProps = {
  feeAmountGBP?: number;
  label?: string;
  ref?: React.Ref<ServiceFeeAppliesNoticeHandle>;
  serviceFeeTiers?: ServiceFeeTiersResponse;
  showFee?: boolean;
  variant?: 'small' | 'regular';
};

export const ServiceFeeAppliesNotice: React.FC<ServiceFeeAppliesNoticeProps> = ({
  feeAmountGBP,
  label = LocalT.translate('serviceFeeDetailsWrapper.noticeLabel') as string,
  ref,
  serviceFeeTiers,
  showFee = true,
  variant = 'regular',
}) => {
  const { vehicleLookupState: vehicle } = useContext(VehicleLookupContext);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { OPTIMIZELY_TRACK } = useOptimizelyTrack();

  const dpEnquiryId = vehicle?.enquiry?.dpEnquiryId;

  const openModal = useCallback(() => {
    OPTIMIZELY_TRACK(
      EVENT.SERVICE_FEE_EXPLAINER_MODAL_CLICKED,
      {
        customId: String(dpEnquiryId),
      },
      {
        action: typeof feeAmountGBP === 'number' ? 'valuation' : 'confirm_sale',
      },
    );
    setIsModalOpen(true);
  }, [OPTIMIZELY_TRACK, dpEnquiryId, feeAmountGBP]);

  const onCloseModal = useCallback(() => setIsModalOpen(false), []);

  useImperativeHandle(ref, () => ({
    openModal,
  }), [openModal]);

  const InfoButton: IconComponentFunction = () => (
    <Button
      key="info"
      data-thc-icon
      data-testid="service-fee-notice-info-trigger"
      icon={() => <InfoIcon filled={false} size={variant === 'regular' ? SIZE.SMALL : SIZE.XSMALL} />}
      label={LocalT.translate('serviceFeeAppliesNotice.infoLabel') as string}
      onClick={openModal}
      showLabel={false}
      size={SIZE.SMALL}
      variant={VARIANT.TERTIARY}
    />
  );

  const getContent = () => {
    if (!showFee) {
      return LocalT.translate('serviceFeeAppliesNotice.noticeWithoutFee', { label });
    }
    if (feeAmountGBP === undefined) {
      return LocalT.translate('serviceFeeAppliesNotice.noticeWithFee', {
        fee: '\u2007\u2007\u2007\u2007',
        label,
      });
    }
    if (feeAmountGBP !== 0) {
      return LocalT.translate('serviceFeeAppliesNotice.noticeWithFee', {
        fee: `£${feeAmountGBP.toLocaleString()}`,
        label,
      });
    }
    return LocalT.translate('serviceFeeDetailsWrapper.feeMayApplyLabel');
  };

  const content = getContent();
  return (
    <div className={styles.component}>
      <IconText
        data-thc-icontext
        reverse
        data-testid="service-fee-applies-notice"
        icon={InfoButton}
        text={content}
        variant={`small--${variant}`}
      />
      {isModalOpen && (
        <Modal
          centered
          className={styles.container}
          contentClass={styles.modalContent}
          dialogClass={styles.modal}
          dismissModal={onCloseModal}
          size={SIZE.SMALL}
          withCloseButton={true}
        >
          <ServiceFeeExplainer {...{ serviceFeeTiers }} />
        </Modal>
      )}
    </div>
  );
};
